import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { isProductKind } from 'utils/getProductNumber';
import { PRODUCT_NUMBER_IL } from 'utils/constants';
import { useLocation } from 'react-router-dom';
import isLogged from 'utils/isLogged';
import useError from 'hooks/useError';
import useLatestLoan from 'hooks/webapi/useLatestLoan';
import ValidationError from 'utils/ValidationError';

type Props = {
  children: ReactNode;
};

type DR_INSTALLMENT = {
  amount: number;
  dueDate: string;
  startDate: string;
  status: 'DELAYED' | 'PAID' | 'CANCELLED' | 'WRITTEN_OFF';
};

type LoanDR = {
  expirationDate: string;
  installments: DR_INSTALLMENT[];
};

export type ClientDebtRestructuringData = {
  status:
    | 'ELIGIBLE'
    | 'ELIGIBLE_INITIATED'
    | 'IN_PROCESS'
    | 'IN_PROCESS_SIGNED'
    | 'IN_PROCESS_PAID'
    | 'ACTIVE'
    | 'ACTIVE_KEPT'
    | 'NOT_ELIGIBLE';
  isPaymentReceived: boolean;
  isSigned: boolean;
  RepaymentProposalInfo: any;
  commissionAmount: number;
  latestRepaymentProposal: LoanDR;
};

type ClientDebtRestructuringContextType = {
  debtRestructuringData?: ClientDebtRestructuringData | undefined | null;
  fetchDebtRestructuringData: () => Promise<ClientDebtRestructuringData | void>;
  noActionNeeded: boolean;
  isSigned: boolean;
  isPaymentReceived: boolean;
  isDebtRestructuring: boolean;
  nextPaymentNumber: number;
  installmentsPaid: number;
  loading: boolean;
};

const ClientDebtRestructuringContext =
  createContext<ClientDebtRestructuringContextType>(
    {} as ClientDebtRestructuringContextType,
  );

const useClientDebtRestructuring = (): ClientDebtRestructuringContextType =>
  useContext(ClientDebtRestructuringContext);

const ClientDebtRestructuringProvider = ({ children }: Props): JSX.Element => {
  const [loading, startTransition] = useTransition();
  const { latestLoan } = useLatestLoan();
  const [debtRestructuringData, setDebtRestructuringData] = useState<
    ClientDebtRestructuringData | undefined | null
  >();
  const { showError } = useError();
  const location = useLocation();

  const isSigned =
    debtRestructuringData?.isSigned ||
    debtRestructuringData?.status === 'IN_PROCESS_SIGNED';

  const isPaymentReceived =
    debtRestructuringData?.isPaymentReceived ||
    debtRestructuringData?.status === 'IN_PROCESS_PAID';

  const noActionNeeded =
    isPaymentReceived &&
    isSigned &&
    (debtRestructuringData?.status || '').includes('ACTIVE');

  useEffect(() => {
    const prefetch = async () => {
      if (debtRestructuringData === undefined && !loading && isLogged()) {
        startTransition(async () => {
          await fetchDebtRestructuringData();
        });
      }
    };
    prefetch();
  }, [debtRestructuringData, loading, location, startTransition]);

  const fetchDebtRestructuringData =
    useCallback(async (): Promise<ClientDebtRestructuringData | void> => {
      try {
        const fetchedClientDebtRestructuring = await axiosInstance.get(
          '/client/loans/latest/debt-restructuring',
        );
        setDebtRestructuringData(fetchedClientDebtRestructuring.data);
        return fetchedClientDebtRestructuring.data;
      } catch (e) {
        setDebtRestructuringData(null);
        if ((e as ValidationError)?.message !== '404') {
          showError(e as ValidationError);
        }
      }
    }, [showError]);

  const isDebtRestructuring =
    Boolean(debtRestructuringData) &&
    debtRestructuringData?.status !== 'NOT_ELIGIBLE';

  // PAYMENT/INSTALLMENTS INFO
  const TOTAL_PAYMENTS_COUNT = isProductKind(latestLoan, PRODUCT_NUMBER_IL)
    ? 8
    : 6;
  const installmentsPaid =
    TOTAL_PAYMENTS_COUNT -
    (debtRestructuringData?.latestRepaymentProposal?.installments?.length || 0);

  const nextPaymentNumber = installmentsPaid + 1;

  const ClientDebtRestructuringContextValue = useMemo(
    () => ({
      debtRestructuringData,
      fetchDebtRestructuringData,
      noActionNeeded,
      isSigned,
      isPaymentReceived,
      isDebtRestructuring,
      nextPaymentNumber,
      installmentsPaid,
      loading,
    }),
    [
      debtRestructuringData,
      fetchDebtRestructuringData,
      noActionNeeded,
      isSigned,
      isPaymentReceived,
      isDebtRestructuring,
      nextPaymentNumber,
      installmentsPaid,
      loading,
    ],
  );

  return (
    <ClientDebtRestructuringContext.Provider
      value={ClientDebtRestructuringContextValue}
    >
      {children}
    </ClientDebtRestructuringContext.Provider>
  );
};

export {
  useClientDebtRestructuring as default,
  ClientDebtRestructuringProvider,
};
